import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import kotlinx.browser.window
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.ContentBuilder
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.renderComposable
import org.w3c.dom.HTMLDivElement
import org.w3c.dom.events.Event

fun main() {
    renderComposable(rootElementId = "root") { App() }
}

/**
 * Everything the app needs before it can show a screen, in order: a session token (redeemed from
 * the shared link, SPECIFIKACE.md ch. 7), then a default participant for this device (ch. 8
 * point 1). Each gate renders on its own until it is satisfied.
 */
@Composable
fun App() {
    var screen by remember { mutableStateOf(screenForPath(window.location.pathname)) }
    var navigatingBack by remember { mutableStateOf(false) }
    var token by remember { mutableStateOf(loadSessionToken()) }
    var defaultParticipantId by remember { mutableStateOf(loadDefaultParticipantId()) }
    var redeemError by remember { mutableStateOf<String?>(null) }
    var redeeming by remember { mutableStateOf(false) }
    // On a phone the sidebar is a drawer behind the hamburger (see the media query in styles.css).
    var sidebarOpen by remember { mutableStateOf(false) }

    fun endSession() {
        token = null
        saveSessionToken(null)
    }

    // The shared link carries `?invite=<token>`: swap it for a JWT, keep that, and drop the invite
    // from the address bar so it does not linger in history.
    LaunchedEffect(Unit) {
        val invite = inviteTokenFromUrl() ?: return@LaunchedEffect
        redeeming = true
        try {
            token = Api.redeemInvite(invite).also { saveSessionToken(it) }
            redeemError = null
            clearInviteFromUrl()
        } catch (e: Throwable) {
            redeemError = e.apiMessage()
        } finally {
            redeeming = false
        }
    }

    // Keep the address bar in step with the current screen, without pushing a duplicate entry when
    // the change came from the Back button in the first place.
    LaunchedEffect(screen) {
        if (navigatingBack) {
            navigatingBack = false
        } else if (window.location.pathname != screen.path) {
            window.history.pushState(null, "", screen.path)
        }
    }

    DisposableEffect(Unit) {
        val onPopState: (Event) -> Unit = {
            navigatingBack = true
            screen = screenForPath(window.location.pathname)
        }
        window.addEventListener("popstate", onPopState)
        onDispose { window.removeEventListener("popstate", onPopState) }
    }

    val currentToken = token
    when {
        redeeming -> BarePage { P { Text("Ověřuji odkaz…") } }

        currentToken == null -> BarePage {
            H2 { Text("SG Cvičení") }
            ErrorMessage(redeemError)
            P {
                Text(
                    "Tahle appka se otevírá jen přes sdílený odkaz party. Bez něj je jen ke čtení " +
                        "(zatím žádná veřejná data k zobrazení).",
                )
            }
        }

        defaultParticipantId == null -> BarePage {
            OnboardingScreen(token = currentToken, onPicked = {
                defaultParticipantId = it
                saveDefaultParticipantId(it)
            })
        }

        else -> CompositionLocalProvider(LocalSessionExpired provides ::endSession) {
            Div(attrs = { classes("app-shell") }) {
                Sidebar(
                    screen = screen,
                    open = sidebarOpen,
                    onSwitchUser = {
                        defaultParticipantId = null
                        saveDefaultParticipantId(null)
                    },
                    onNavigate = {
                        screen = it
                        sidebarOpen = false
                    },
                    onClose = { sidebarOpen = false },
                )
                Div(attrs = { classes("content") }) {
                    Div(attrs = { classes("topbar") }) {
                        Button(attrs = {
                            classes("menu-toggle")
                            attr("aria-label", "Menu")
                            onClick { sidebarOpen = !sidebarOpen }
                        }) { Text("☰") }
                        H2 { Text(screen.title) }
                    }
                    Div(attrs = { classes("page") }) {
                        CurrentScreen(screen, currentToken, defaultParticipantId!!) { screen = it }
                    }
                }
            }
        }
    }
}

@Composable
private fun CurrentScreen(
    screen: Screen,
    token: String,
    defaultParticipantId: String,
    onNavigate: (Screen) -> Unit,
) {
    when (screen) {
        Screen.DailyEntry -> DailyEntryScreen(token, participantId = defaultParticipantId)
        Screen.Grid -> GridScreen(token, actingParticipantId = defaultParticipantId)
        Screen.Leaderboard -> LeaderboardScreen(token) { onNavigate(Screen.History(it)) }
        Screen.Challenges -> ChallengesScreen(token, defaultParticipantId)
        Screen.Statistics -> StatisticsScreen(token)
        Screen.Admin -> AdminScreen(token)
        is Screen.History -> HistoryScreen(token, screen.participantId)
    }
}

/** The chrome-free shell used by the pre-login and onboarding gates. */
@Composable
private fun BarePage(content: ContentBuilder<HTMLDivElement>) {
    Div(attrs = { classes("app-shell") }) {
        Div(attrs = { classes("content") }) {
            Div(attrs = { classes("page") }, content = content)
        }
    }
}

/**
 * Navigation. On a phone this is a drawer opened by the hamburger and closed by tapping the
 * backdrop; on a wider screen the media query pins it open and `open` no longer matters.
 */
@Composable
private fun Sidebar(
    screen: Screen,
    open: Boolean,
    onSwitchUser: () -> Unit,
    onNavigate: (Screen) -> Unit,
    onClose: () -> Unit,
) {
    Div(attrs = {
        classes("sidebar-backdrop")
        if (open) classes("open")
        onClick { onClose() }
    }) {}
    Div(attrs = {
        classes("sidebar")
        if (open) classes("open")
    }) {
        Div(attrs = { classes("brand") }) {
            Div(attrs = { classes("brand-ball") }) {}
            H1 { Text("SG Cvičení") }
        }
        Div(attrs = { classes("nav") }) {
            listOf(Screen.DailyEntry, Screen.Grid, Screen.Leaderboard, Screen.Challenges, Screen.Statistics, Screen.Admin).forEach { target ->
                NavButton(target.title, screen == target) { onNavigate(target) }
            }
        }
        // The device remembers who it writes for; this is the only way to hand the phone to
        // somebody else, so it belongs in the menu rather than behind the admin password.
        Button(attrs = {
            classes("switch-user")
            onClick { onSwitchUser() }
        }) { Text("⟲ Změnit uživatele") }
    }
}

/** "Who are you?" -- picks the participant this device writes for from then on. */
@Composable
private fun OnboardingScreen(token: String, onPicked: (String) -> Unit) {
    val state = rememberAsyncState()
    var participants by remember { mutableStateOf<List<Participant>>(emptyList()) }
    var newName by remember { mutableStateOf("") }

    LaunchedEffect(Unit) { state.run { participants = Api.fetchParticipants() } }

    fun addAndPick() {
        val name = newName.trim()
        if (name.isBlank()) return
        state.launch {
            val created = Api.createParticipant(token, name)
            newName = ""
            onPicked(created.id)
        }
    }

    Div(attrs = { classes("onboarding") }) {
        H2 { Text("Kdo jsi?") }
        P {
            Text(
                "Vyber se ze seznamu -- tohle zařízení si tě zapamatuje jako výchozího uživatele " +
                    "pro denní zápis (jde to později změnit v menu).",
            )
        }
        ErrorMessage(state.error)

        when {
            !state.loaded -> LoadingText()
            participants.isEmpty() -> P { Text("Zatím tu nikdo není -- přidej první jméno níže.") }
            else -> Div(attrs = { classes("onboarding-grid") }) {
                participants.forEach { participant ->
                    Div(attrs = {
                        classes("onboarding-card")
                        onClick { onPicked(participant.id) }
                    }) { Text(participant.name) }
                }
            }
        }

        Div(attrs = { classes("input-row", "onboarding-add") }) {
            TextField(newName, { newName = it }, placeholder = "Nové jméno…", onEnter = ::addAndPick)
            PrimaryButton(
                if (state.busy) "Přidávám…" else "Přidat a vybrat",
                enabled = newName.isNotBlank() && !state.busy,
                onClick = ::addAndPick,
            )
        }
    }
}
